package com.xtream.composeapp.presentation.ui.search

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.focusable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.wrapContentWidth
import androidx.compose.foundation.lazy.staggeredgrid.LazyHorizontalStaggeredGrid
import androidx.compose.foundation.lazy.staggeredgrid.StaggeredGridCells
import androidx.compose.foundation.lazy.staggeredgrid.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.outlined.ArrowBack
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.outlined.CalendarMonth
import androidx.compose.material.icons.outlined.Category
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material.icons.outlined.Schedule
import androidx.compose.material.icons.outlined.Sensors
import androidx.compose.material.icons.outlined.SortByAlpha
import androidx.compose.material.icons.outlined.Star
import androidx.compose.material3.Button
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.RangeSlider
import androidx.compose.material3.SliderDefaults
import androidx.compose.material3.Surface
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusProperties
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.graphics.vector.rememberVectorPainter
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.paging.compose.LazyPagingItems
import androidx.paging.compose.collectAsLazyPagingItems
import androidx.paging.compose.itemKey
import com.xtream.sharedResources.resources.Res
import com.xtream.sharedResources.resources.clapper_open
import com.xtream.sharedResources.resources.home_2
import com.xtream.sharedResources.resources.screen_ic
import com.xtream.data_kmp.db.dao.LiveSortBy
import com.xtream.data_kmp.db.dao.MovieSortBy
import com.xtream.data_kmp.db.dao.SeriesSortBy
import com.xtream.data_kmp.util.extension.toggle
import com.xtream.ui.ui.widget.XAssistChip
import org.jetbrains.compose.resources.painterResource

// ── Header ───────────────────────────────────────────────────────────────────

@Composable
  fun FilterSheetHeader(
    onReset: () -> Unit,
    onDismiss: () -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp, vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        IconButton(onClick = onDismiss) {
            Icon(Icons.AutoMirrored.Outlined.ArrowBack, contentDescription = "Back")
        }
        Text(
            text = "Filter",
            style = MaterialTheme.typography.titleMedium,
            modifier = Modifier.weight(1f).padding(start = 4.dp),
        )
        TextButton(onClick = onReset) {
            Text("Reset all", style = MaterialTheme.typography.labelMedium)
        }
    }
}

// ── Tab row ──────────────────────────────────────────────────────────────────
@Composable
  fun FilterTabRow(
    activeTab: FilterTab,
    onTabSelected: (FilterTab) -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 14.dp, vertical = 10.dp),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        FilterTab.entries.forEach { tab ->
            val selected = tab == activeTab
            val (painter, label) = when (tab) {
                FilterTab.TV      -> painterResource(Res.drawable.home_2)        to "TV"
                FilterTab.MOVIES  -> painterResource(Res.drawable.clapper_open)  to "Movies"
                FilterTab.SERIES  -> painterResource(Res.drawable.screen_ic)     to "Series"
            }
            FilterTabChip(
                painter = painter,
                label = label,
                selected = selected,
                onClick = { onTabSelected(tab) },
                modifier = Modifier.weight(1f),
            )
        }
    }
}

@Composable
private fun FilterTabChip(
    painter: Painter,
    label: String,
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val containerColor = if (selected)
        MaterialTheme.colorScheme.primary
    else
        MaterialTheme.colorScheme.surfaceVariant

    val contentColor = if (selected)
        MaterialTheme.colorScheme.onPrimary
    else
        MaterialTheme.colorScheme.onSurfaceVariant

    Surface(
        onClick = onClick,
        modifier = modifier,
        shape = CircleShape,
        color = containerColor,
        contentColor = contentColor,
    ) {
        Row(
            modifier = Modifier.padding(vertical = 8.dp),
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Icon(
                painter = painter,
                contentDescription = null,
                modifier = Modifier.size(16.dp),
            )
            Spacer(Modifier.width(5.dp))
            Text(label, style = MaterialTheme.typography.labelMedium)
        }
    }
}
// ── Reusable section primitives ───────────────────────────────────────────────

@Composable
private fun FilterSection(
    label: String,
    icon: ImageVector,
    content: @Composable ColumnScope.() -> Unit,
) {
    Column(modifier = Modifier
        .fillMaxWidth()
        .padding(horizontal = 14.dp, vertical = 10.dp)
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.padding(bottom = 8.dp),
        ) {
            Icon(
                icon,
                contentDescription = null,
                modifier = Modifier.size(14.dp),
                tint = MaterialTheme.colorScheme.primary,
            )
            Spacer(Modifier.width(6.dp))
            Text(
                label.uppercase(),
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                letterSpacing = 0.08.sp,
            )
        }
        content()
    }
    HorizontalDivider(thickness = 0.5.dp)
}

sealed interface ChipItems<T> {
    class FromList<T>(val list: List<T>) : ChipItems<T>
    class FromPaging<T : Any>(val pagingItems: LazyPagingItems<T>) : ChipItems<T>
}

// Extension helpers for cleaner call sites
fun <T> List<T>.asChipItems() = ChipItems.FromList(this)
fun <T : Any> LazyPagingItems<T>.asChipItems() = ChipItems.FromPaging(this)
@OptIn(ExperimentalFoundationApi::class)
@Composable
private fun <T : Any> ChipGroup(
    items: ChipItems<T>,
    selectedList: List<T>?,
    label: (T) -> String,
    onSelect: (T) -> Unit,
    modifier: Modifier = Modifier,
) {
    val itemCount = when (items) {
        is ChipItems.FromList -> items.list.size
        is ChipItems.FromPaging -> items.pagingItems.itemCount
    }

    val useTwoRows = false//itemCount > 6

    LazyHorizontalStaggeredGrid(
        rows = StaggeredGridCells.Fixed(if (useTwoRows) 2 else 1),
        modifier = modifier
            .height(if (useTwoRows) 60.dp else 30.dp)
            .wrapContentWidth(),
        horizontalItemSpacing = 7.dp,
        verticalArrangement = Arrangement.spacedBy(7.dp),
    ) {
        when (items) {
            is ChipItems.FromList -> {
                items(items.list) { item ->
                    ChipItem(item, selectedList, label, onSelect)
                }
            }
            is ChipItems.FromPaging -> {
                items(
                    count = items.pagingItems.itemCount,
                    key = items.pagingItems.itemKey(),
                ) { index ->
                    items.pagingItems[index]?.let { item ->
                        ChipItem(item, selectedList, label, onSelect)
                    }
                }
            }
        }
    }
}

@Composable
private fun <T : Any> ChipItem(
    item: T,
    selectedList: List<T>?,
    label: (T) -> String,
    onSelect: (T) -> Unit,
) {
    val isSelected = selectedList?.contains(item)==true
    XAssistChip(
        isSelected = isSelected,
        onClick = { onSelect(item) },
        title = label(item),
        vectorIcon = if (isSelected) rememberVectorPainter(Icons.Default.Check) else null,
    )
}
@Composable
private fun ToggleRow(
    label: String,
    icon: ImageVector,
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 14.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Icon(icon, contentDescription = null,
            modifier = Modifier.size(16.dp),
            tint = MaterialTheme.colorScheme.onSurfaceVariant)
        Spacer(Modifier.width(8.dp))
        Text(label,
            style = MaterialTheme.typography.bodyMedium,
            modifier = Modifier.weight(1f))
        Switch(checked = checked, onCheckedChange = onCheckedChange)
    }
    HorizontalDivider(thickness = 0.5.dp)
}

@Composable
private fun RatingPicker(
    selected: Float?,
    onSelect: (Float?) -> Unit,
) {
    val options = listOf(null, 5f, 6f, 7f, 8f, 9f)
    Row(horizontalArrangement = Arrangement.spacedBy(5.dp)) {
        options.forEach { value ->
            val isSelected = value == selected
            Surface(
                onClick = { onSelect(value) },
                shape = RoundedCornerShape(8.dp),
                color = if (isSelected) MaterialTheme.colorScheme.primaryContainer
                else MaterialTheme.colorScheme.surfaceVariant,
                contentColor = if (isSelected) MaterialTheme.colorScheme.onPrimaryContainer
                else MaterialTheme.colorScheme.onSurfaceVariant,
                border = if (isSelected) BorderStroke(1.dp, MaterialTheme.colorScheme.primary)
                else BorderStroke(0.5.dp, MaterialTheme.colorScheme.outlineVariant),
                modifier = Modifier.size(38.dp),
            ) {
                Column(
                    modifier = Modifier.fillMaxSize(),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.Center,
                ) {
                    Icon(
                        if (value == null) Icons.Outlined.Close else Icons.Outlined.Star,
                        contentDescription = null,
                        modifier = Modifier.size(14.dp),
                    )
                    Text(
                        if (value == null) "any" else "${value.toInt()}+",
                        style = MaterialTheme.typography.labelSmall,
                    )
                }
            }
        }
    }
}

@Composable
fun YearRangeSlider(
    yearFrom: Int,
    yearTo: Int,
    minYear: Int,
    maxYear: Int,
    onRangeChange: (from: Int, to: Int) -> Unit,
) {
    var focusedThumb by remember { mutableStateOf<Int?>(null) }
    val startFocusRequester = remember { FocusRequester() }
    val endFocusRequester = remember { FocusRequester() }

    val totalSteps = maxYear - minYear
    val startFraction = (yearFrom - minYear).toFloat() / totalSteps
    val endFraction = (yearTo - minYear).toFloat() / totalSteps

    Column(modifier = Modifier.padding(horizontal = 16.dp).fillMaxWidth()) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            Text(
                "$minYear",
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            Text(
                "$yearFrom – $yearTo",
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.primary,
                fontWeight = FontWeight.Medium,
            )
            Text(
                "$maxYear",
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }

        Spacer(Modifier.height(8.dp))

        BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
            val trackWidth = maxWidth
            val thumbSize = 40.dp

            RangeSlider(
                value = yearFrom.toFloat()..yearTo.toFloat(),
                onValueChange = { range ->
                    onRangeChange(range.start.toInt(), range.endInclusive.toInt())
                },
                colors = SliderDefaults.colors(
                    thumbColor = Color.Transparent,
                ),
                valueRange = minYear.toFloat()..maxYear.toFloat(),
                steps = totalSteps - 1,
                modifier = Modifier
                    .fillMaxWidth()
                    .focusProperties { canFocus = false },
            )

            // Start thumb overlay ▶
            Box(
                contentAlignment = Alignment.Center,
                modifier = Modifier
                    .size(thumbSize)
                    .offset(x = trackWidth * startFraction - thumbSize / 2)
                    .align(Alignment.CenterStart)
                    .focusRequester(startFocusRequester)
                    .onFocusChanged {
                        focusedThumb = if (it.isFocused) 0 else {
                            if (focusedThumb == 0) null else focusedThumb
                        }
                    }
                    .focusable()
                    .onKeyEvent { event ->
                        if (event.type != KeyEventType.KeyDown) return@onKeyEvent false
                        when (event.key) {
                            Key.DirectionLeft   -> { if (yearFrom > minYear) onRangeChange(yearFrom - 1, yearTo); true }
                            Key.DirectionRight  -> { if (yearFrom < yearTo) onRangeChange(yearFrom + 1, yearTo); true }
                            Key.Enter,
                            Key.NumPadEnter,
                            Key.DirectionCenter -> { endFocusRequester.requestFocus(); true }
                            else                -> false
                        }
                    }
                    .drawBehind {
                        // Always draw circle background
                        drawCircle(
                            color = if (focusedThumb == 0)
                                Color.White.copy(alpha = 0.2f)
                            else
                                Color.White.copy(alpha = 0.08f),
                            radius = size.minDimension / 2,
                        )
                        // Focus ring
                        if (focusedThumb == 0) {
                            drawCircle(
                                color = Color.White.copy(alpha = 0.3f),
                                radius = size.minDimension / 2,
                                style = Stroke(width = 2.dp.toPx()),
                            )
                        }
                    },
            ) {
                Text(
                    text = "▶",
                    style = MaterialTheme.typography.labelSmall,
                    color = if (focusedThumb == 0)
                        MaterialTheme.colorScheme.primary
                    else
                        MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.6f),
                )
            }

            // End thumb overlay ◀
            Box(
                contentAlignment = Alignment.Center,
                modifier = Modifier
                    .size(thumbSize)
                    .offset(x = trackWidth * endFraction - thumbSize / 2)
                    .align(Alignment.CenterStart)
                    .focusRequester(endFocusRequester)
                    .onFocusChanged {
                        focusedThumb = if (it.isFocused) 1 else {
                            if (focusedThumb == 1) null else focusedThumb
                        }
                    }
                    .focusable()
                    .onKeyEvent { event ->
                        if (event.type != KeyEventType.KeyDown) return@onKeyEvent false
                        when (event.key) {
                            Key.DirectionLeft   -> { if (yearTo > yearFrom) onRangeChange(yearFrom, yearTo - 1); true }
                            Key.DirectionRight  -> { if (yearTo < maxYear) onRangeChange(yearFrom, yearTo + 1); true }
                            Key.Enter,
                            Key.NumPadEnter,
                            Key.DirectionCenter -> { startFocusRequester.requestFocus(); true }
                            else                -> false
                        }
                    }
                    // End thumb overlay ◀
                    .drawBehind {
                        drawCircle(
                            color = if (focusedThumb == 1)  // ← was 0
                                Color.White.copy(alpha = 0.2f)
                            else
                                Color.White.copy(alpha = 0.08f),
                            radius = size.minDimension / 2,
                        )
                        if (focusedThumb == 1) {  // ← was 0
                            drawCircle(
                                color = Color.White.copy(alpha = 0.3f),
                                radius = size.minDimension / 2,
                                style = Stroke(width = 2.dp.toPx()),
                            )
                        }
                    },
            ) {
                Text(
                    text = "◀",
                    style = MaterialTheme.typography.labelSmall,
                    color = if (focusedThumb == 1)
                        MaterialTheme.colorScheme.primary
                    else
                        MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.6f),
                )
            }
        }
    }
}
// ── Bottom bar ────────────────────────────────────────────────────────────────

@Composable
private fun FilterBottomBar(
    onClear: () -> Unit,
    onApply: () -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 14.dp, vertical = 12.dp),
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        OutlinedButton(
            onClick = onClear,
            modifier = Modifier.weight(1f),
            shape = CircleShape,
        ) { Text("Clear") }

        Button(
            onClick = onApply,
            modifier = Modifier.weight(2f),
            shape = CircleShape,
        ) { Text("Show results") }
    }
}
// ── Series pane ──────────────────────────────────────────────────────────────

@Composable
  fun SeriesFilterPane(state: SearchSeriesState,

                             onUpdate: (SearchSeriesState) -> Unit,
                             searchScreenViewModel: SearchScreenViewModel) {
    FilterSection("Sort by", Icons.Outlined.SortByAlpha) {
        ChipGroup(
            items = SeriesSortBy.entries.asChipItems(),
            selectedList = listOf(state.sortBy),
            label = { it.label() },
            onSelect = { onUpdate(state.copy(sortBy = it)) },
        )
    }

    FilterSection("Genre", Icons.Outlined.Category) {
        val groups = searchScreenViewModel.seriesCategoriesPagingFlow.collectAsLazyPagingItems()
        val selectedGroups by searchScreenViewModel.selectedSeriesCategoryFlow.collectAsStateWithLifecycle(null)
        ChipGroup(
            items = groups.asChipItems(),

            selectedList = selectedGroups ,
            label = { it.categoryName?:""  },
            onSelect = {category->
                //  onUpdate(state.copy(tvCategories = state.tvCategories.toggle(it)))

                onUpdate(state.copy(selectedCategories= state.selectedCategories.orEmpty().toggle(category)))
            },
        )
    }
    FilterSection("Release year", Icons.Outlined.CalendarMonth) {
        YearRangeSlider(
            state.range.first,
            state.range.last,
            minYear = SearchDefaults.FULL_YEAR_RANGE_SERIES.first,
            maxYear = SearchDefaults.FULL_YEAR_RANGE_SERIES.last,
            onRangeChange = { from, to -> onUpdate(state.copy(range = IntRange(from,  to))) },
        )
    }
    /*FilterSection("Min rating", Icons.Outlined.Star) {
        RatingPicker(state.seriesMinRating) { onUpdate(state.copy(seriesMinRating = it)) }
    }
    FilterSection("Seasons", Icons.Outlined.Layers) {
        ChipGroup(
            items = SeriesSeasons.entries,
            selectedList = state.seriesSeasons,
            label = { it.label() },
            onSelect = { onUpdate(state.copy(seriesSeasons = state.seriesSeasons.toggle(it))) },
        )
    }
    ToggleRow("Favorites only", Icons.Outlined.BookmarkBorder,
        state.seriesFavoritesOnly) { onUpdate(state.copy(seriesFavoritesOnly = it)) }*/
}
// ── Movies pane ──────────────────────────────────────────────────────────────

@Composable
  fun MoviesFilterPane(
    state: SearchMovieState,

    onUpdate: (SearchMovieState) -> Unit,
    searchScreenViewModel: SearchScreenViewModel
) {
    FilterSection("Sort by", Icons.Outlined.SortByAlpha) {
        ChipGroup(
            items = MovieSortBy.entries.asChipItems(),
            selectedList = listOf(state.sortBy),
            label = { it.label() },
            onSelect = {
                println("VodStreamsDao SQL: $it")  // check Logcat

                onUpdate(state.copy(sortBy = it)) },
        )
    }
    FilterSection("Genre", Icons.Outlined.Category) {
        val groups = searchScreenViewModel.vodCategoriesPagingFlow.collectAsLazyPagingItems()
        val selectedGroups by searchScreenViewModel.selectedVodCategoryFlow.collectAsStateWithLifecycle(null)
        ChipGroup(
            items = groups.asChipItems(),

            selectedList = selectedGroups ,
            label = { it.categoryName?:""  },
            onSelect = {category->
                //  onUpdate(state.copy(tvCategories = state.tvCategories.toggle(it)))

                onUpdate(state.copy(selectedCategories= state.selectedCategories.orEmpty().toggle(category)))
            },
        )
    }
    FilterSection("Added in (press ok to switch)", Icons.Outlined.CalendarMonth) {
        YearRangeSlider(
            state.range.first,
            state.range.last,
            minYear = SearchDefaults.FULL_YEAR_RANGE_MOVIE.first,
            maxYear =SearchDefaults.FULL_YEAR_RANGE_MOVIE.last,
            onRangeChange = { from, to -> onUpdate(state.copy(range = IntRange(from,  to))) },
        )
    }
    FilterSection("Min rating", Icons.Outlined.Star) {
        // RatingPicker(state.movieMinRating) { onUpdate(state.copy(movieMinRating = it)) }
    }
    FilterSection("Duration", Icons.Outlined.Schedule) {
        /* ChipGroup(
             items = MovieDuration.entries,
             selectedList = listOf(state.movieDuration),
             label = { it.label() },
             onSelect = { onUpdate(state.copy(movieDuration = it)) },
         )*/
    }
    //  ToggleRow("Favorites only", Icons.Outlined.BookmarkBorder,
    //    state.movieFavoritesOnly) { onUpdate(state.copy(movieFavoritesOnly = it)) }
}
// ── TV pane ──────────────────────────────────────────────────────────────────

@Composable
  fun TvFilterPane(
    state: SearchLiveState,

    onUpdate: (SearchLiveState) -> Unit,
    searchScreenViewModel: SearchScreenViewModel
) {

    FilterSection("Sort by", Icons.Outlined.SortByAlpha) {
        ChipGroup(
            items = LiveSortBy.entries.asChipItems(),
            selectedList = listOf(state.sortBy),
            label = { it.label() },
            onSelect = { onUpdate(state.copy(sortBy = it)) },
        )
    }
    FilterSection("Stream type", Icons.Outlined.Sensors) {

        ChipGroup(
            items = TvStreamType.entries.asChipItems(),
            selectedList = listOf(state.type),
            label = { it.label() },
            onSelect = { onUpdate(state.copy(type = it)) },
        )
    }
    FilterSection("Category", Icons.Outlined.Category) {
        val groups = searchScreenViewModel.liveCategoriesPagingFlow.collectAsLazyPagingItems()
        val selectedGroups by searchScreenViewModel.selectedLiveCategoryFlow.collectAsStateWithLifecycle(null)
        ChipGroup(
            items = groups.asChipItems(),

            selectedList = selectedGroups ,
            label = { it.categoryName ?:""  },
            onSelect = {category->
                //  onUpdate(state.copy(tvCategories = state.tvCategories.toggle(it)))

                onUpdate(state.copy(selectedCategories= state.selectedCategories.orEmpty().toggle(category)))
            },
        )


    }
    /* ToggleRow("EPG available only", Icons.Outlined.CalendarMonth,
         state.tvEpgOnly) { onUpdate(state.copy(tvEpgOnly = it)) }
     ToggleRow("Favorites only", Icons.Outlined.BookmarkBorder,
         state.tvFavoritesOnly) { onUpdate(state.copy(tvFavoritesOnly = it)) }*/
}